import { Monitor, Moon, Sun } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarMenuButton } from '@/components/ui/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useTheme, type Theme } from '@/components/theme-provider';

const OPTIONS: { value: Theme; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Claro', icon: Sun },
  { value: 'dark', label: 'Oscuro', icon: Moon },
  { value: 'system', label: 'Sistema', icon: Monitor },
];

// Toggle de tema en el footer del sidebar. R4b (Ajuste 31): el contenido del
// menú pasa a DropdownMenuRadioGroup / DropdownMenuRadioItem TAL CUAL
// (c-dropdown-menu-7, con aria-checked correcto — antes era un DropdownMenuItem
// suelto por opción, sin semántica de radio); sin `title` nativo. El ícono
// refleja la opción ELEGIDA (no el tema resuelto): Monitor cuando está en
// Sistema.
//
// El trigger es un SidebarMenuButton (familia Sidebar), no un Button ghost
// reestilado a mano con tokens --sidebar-* (Ajuste 17: esa lista de
// adaptaciones de color está cerrada). SidebarMenuButton ya pinta con
// text-sidebar-foreground/hover:bg-sidebar-accent solo — igual que el
// AccountButton del pie de c-sidebar-2 —, tal cual el vendorizado; acá solo
// se ajusta layout (tamaño cuadrado, sin el label de lista) vía className,
// nunca color.
export function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const ActiveIcon = OPTIONS.find(opt => opt.value === theme)?.icon ?? Monitor;

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger
          render={
            <DropdownMenuTrigger
              render={
                // Decisión 6 de Juan (28/9, qa_visual_reui_2026-09-28.md §7):
                // caja con borde como en la rc.1, mismo criterio que el
                // buscador (sidebar.tsx, U-8) — bg-sidebar para que el
                // variant="outline" del vendorizado (bg-background) no
                // desentone contra los tokens --sidebar-* de la sidebar. A
                // diferencia del buscador, este botón en la rc.1 no tenía
                // relleno en reposo — solo borde, con hover:bg-white/5 recién
                // al pasar el mouse. bg-sidebar (mismo color que el fondo de
                // la sidebar) ya reproduce ese "sin relleno": pasarlo a
                // bg-sidebar-accent como el buscador sería una regresión, no
                // un fix.
                <SidebarMenuButton
                  aria-label="Cambiar tema"
                  variant="outline"
                  className="size-8 justify-center bg-sidebar p-0"
                />
              }
            />
          }
        >
          <ActiveIcon aria-hidden="true" />
        </TooltipTrigger>
        <TooltipContent side="right">Cambiar tema</TooltipContent>
      </Tooltip>
      <DropdownMenuContent side="right" align="end" sideOffset={12} className="w-40">
        <DropdownMenuGroup>
          <DropdownMenuLabel>Tema</DropdownMenuLabel>
          <DropdownMenuRadioGroup value={theme} onValueChange={value => setTheme(value as Theme)}>
            {OPTIONS.map(opt => (
              <DropdownMenuRadioItem key={opt.value} value={opt.value}>
                <opt.icon aria-hidden="true" />
                {opt.label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
